import Swiper from 'swiper';
import {Autoplay, FreeMode} from 'swiper/modules';


export default class DenhaagHeroCarousel {
  constructor(id = 'denhaag-hero--carousel') {
    this.id = id;
    this.hero = document.getElementById( id );

    if (!this.hero) {
      return;
    }

    this.slider = null;
    this.tickerSpeed = 5000; // Autoplay delay in ms (lower = faster)
    this.isPaused = false;

    // Minimum slides needed for the breakpoints.
    this.amountPerBreakpoint = {
      0: 4,
      768: 6,
      1024: 10,
      1768: 12,
    }

    this.button = this.hero.querySelector( `.${this.id}__control` );

    this.labels = {
      play: 'Play',
      pause: 'Pause'
    };

    if (this.button) {
      this.labels = {...this.labels, ...JSON.parse( this.button.dataset.labels )};
    }
  }

  /**
   * Initialize slider.
   */
  initSlider() {
    if (!this.hero) {
      return;
    }

    const el = this.hero.querySelector( `.${this.id}__items` );

    if (!el) {
      return;
    }

    this.maybeAddSlides();

    this.createSwiper( el );
    this.maybePlay();

    this.events();
  }

  /**
   * Maybe play the slider based on user preference.
   */

  maybePlay() {
    if (!window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches) {
      if (this.slider && this.slider.autoplay) {
        this.slider.autoplay.start();
      }
      this.isPaused = false;
      this.updateButton();
    } else {
      this.pause();
    }
  }

  /**
   * Slider events.
   */
  events() {
    if (this.button) {
      this.button.addEventListener( 'click', () => {
        if (!!this.isPaused) {
          this.play();
        } else {
          this.pause();
        }
      } );
    }
  }

  /**
   * Maybe add slides is fired before initializing the slider.
   */
  maybeAddSlides() {
    const el = this.hero.querySelector( `.${this.id}__items` );

    if (!el) {
      return;
    }

    const wrapper = el.querySelector( '.swiper-wrapper' );
    if (!wrapper) {
      return;
    }

    const breakpoints = Object.entries( this.amountPerBreakpoint );
    const slidesLength = wrapper.children.length;
    const lastIndex = breakpoints.length - 1;

    // Loop through each breakpoint and check the minimum amound of slides required.
    breakpoints.forEach( (object, index) => {
      let mq = index === lastIndex ? `(min-width: ${object[0]}px)` : `(min-width: ${object[0]}px) AND (max-width: ${Math.floor( breakpoints[index + 1][0] - 1 )}px)`;

      // Matches media-query and amount of slides are to few and duplicate them.
      if (!!window.matchMedia( mq ).matches && slidesLength < object[1] && 1 > slidesLength / object[1]) {
        wrapper.innerHTML += wrapper.innerHTML + wrapper.innerHTML;
      }

      if (0 === index && !!window.matchMedia( mq ).matches) {
        this.mobileOffset();
      } else if (0 === index && !window.matchMedia( mq ).matches) {
        document.body.style.removeProperty( '--denhaag-hero-carousel-items-top' );
      }
    } );
  }

  /**
   * Create Swiper instance on given element.
   */
  createSwiper( el ) {
    this.slider = new Swiper( el, {
      modules: [Autoplay, FreeMode],
      slidesPerView: 'auto',
      spaceBetween: 30,
      loop: true,
      freeMode: {
        enabled: true,
        momentum: false,
      },
      autoplay: {
        delay: 0,
        disableOnInteraction: false,
      },
      speed: this.tickerSpeed,
      allowTouchMove: false,
      direction: 'horizontal',
    } );
  }

  /**
   * Pause the slider.
   */
  pause() {
    this.isPaused = true;
    this.hero.classList.add( `${this.id}--is-paused` );

    if (this.slider) {
      if (this.slider.autoplay) {
        this.slider.autoplay.stop();
      }
      // Freeze at current visual position.
      const computedTransform = getComputedStyle( this.slider.wrapperEl ).transform;
      this.slider.wrapperEl.style.setProperty( 'transition-duration', '0ms' );
      this.slider.wrapperEl.style.setProperty( 'transform', computedTransform );
    }

    this.updateButton();
  }

  /**
   * Play the slider if paused.
   */
  play() {
    this.isPaused = false;
    this.hero.classList.remove( `${this.id}--is-paused` );

    if (this.slider) {
      const el = this.slider.el;
      this.slider.destroy( true, false );
      this.createSwiper( el );
      if (this.slider.autoplay) {
        this.slider.autoplay.start();
      }
    }

    this.updateButton();
  }

  /**
   * Reflect the current state on the control, the label is the only state indicator.
   */
  updateButton() {
    if (this.button) {
      this.button.setAttribute( 'aria-label', this.isPaused ? this.labels.play : this.labels.pause );
    }
  }

  /**
   * Resize Event pausing the slider to make sure we don't get a buggy carousel.
   */
  resize() {
    if (!this.slider) {
      this.initSlider();
      return;
    }

    this.pause();

    if (!!window.matchMedia( '(max-width: 768px)' ).matches) {
      this.mobileOffset();
    } else {
      document.body.style.removeProperty( '--denhaag-hero-carousel-items-top' );
    }

    // Update Swiper on resize
    if (this.slider) {
      this.slider.update();
    }
  }

  /**
   * Calculate mobile offset.
   */
  mobileOffset() {
    const content = this.hero.querySelector( `.${this.id}__content` );
    const negativeOffset = 4; // Designed in Figma.
    document.body.style.setProperty( '--denhaag-hero-carousel-items-top', `${Math.ceil( content.offsetHeight - negativeOffset )}px` );
  }
}
